import { FormEvent, useEffect, useState } from "react";
import { verifiedAffiliateEnvironment } from "../../hooks/api/admin/affiliateEnvironment";
import { AffiliateState, AffiliateTier } from "../../interfaces/affiliate";

type Affiliate = AffiliateState["affiliates"][number];
type Operation = "downgrade" | "terminate";
const tierRanks = { VIP1: 1, VIP2: 2, VIP3: 3 };
const tiers: AffiliateTier[] = ["VIP1", "VIP2", "VIP3"];

export function currentAffiliateTier(affiliate: Affiliate, at: number) {
  if (!Number.isFinite(at)) return undefined;
  return (affiliate.tier_changes || []).reduce((tier, change) => {
    if (
      Date.parse(change.effective_at) <= at &&
      (!change.cancelled_at || at < Date.parse(change.cancelled_at))
    )
      return change.tier;
    return tier;
  }, affiliate.initial_tier || affiliate.tier);
}

export function LifecyclePanel({
  data,
  disabled,
  onCommand,
}: {
  data: AffiliateState;
  disabled: boolean;
  onCommand: (operation: Operation, payload: Record<string, unknown>) => void;
}) {
  const [address, setAddress] = useState("");
  const [tier, setTier] = useState<AffiliateTier | "">("");
  const [review, setReview] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const affiliate = data.affiliates.find((item) => item.address === address);
  const at = Date.parse(data.ledger.evaluation_at || "");
  const currentTier = affiliate && currentAffiliateTier(affiliate, at);
  const pending = affiliate?.tier_changes?.find(
    (change) =>
      (!change.announced_at || Date.parse(change.announced_at) <= at) &&
      Date.parse(change.effective_at) > at &&
      (!change.cancelled_at || at < Date.parse(change.cancelled_at))
  );
  const inactive =
    disabled ||
    !verifiedAffiliateEnvironment(data) ||
    !data.enabled ||
    !Number.isFinite(at) ||
    !affiliate ||
    Boolean(affiliate.terminated_at) ||
    (data.enrollment_mode !== "open" && !data.allowlist.includes(address));
  const lowerTiers = currentTier
    ? tiers.filter((candidate) => tierRanks[candidate] < tierRanks[currentTier])
    : [];
  const canDowngrade =
    !inactive && data.capabilities?.downgrade === true && !pending;
  const canTerminate =
    !inactive &&
    data.capabilities?.terminate === true &&
    data.lifecycle_policy !== "launch_v1";
  const reviewKey = `${address}:${currentTier}:${pending?.effective_at || ""}`;
  const reviewing = canTerminate && review === reviewKey;
  useEffect(() => {
    if (!canTerminate) {
      setReview("");
      setConfirmation("");
    }
  }, [canTerminate]);
  const schedule = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canDowngrade || !tier || !lowerTiers.includes(tier)) return;
    onCommand("downgrade", { affiliate: address, tier });
    setTier("");
  };
  const terminate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!reviewing || confirmation.toLowerCase() !== address.toLowerCase())
      return;
    onCommand("terminate", { affiliate: address });
    setReview("");
    setConfirmation("");
  };
  return (
    <section aria-label="Affiliate lifecycle">
      <h2>Affiliate downgrade and termination</h2>
      <label htmlFor="lifecycle-affiliate">
        Existing affiliate
        <select
          id="lifecycle-affiliate"
          value={address}
          disabled={disabled}
          onChange={(event) => {
            setAddress(event.target.value);
            setTier("");
            setReview("");
            setConfirmation("");
          }}
        >
          <option value="">Select an approved affiliate</option>
          {data.affiliates.map((item) => (
            <option key={item.address} value={item.address}>
              {item.address}
              {item.terminated_at ? " (terminated)" : ""}
            </option>
          ))}
        </select>
      </label>
      {!Number.isFinite(at) && (
        <p>Refresh backend state before changing an affiliate’s status.</p>
      )}
      {affiliate && Number.isFinite(at) && (
        <p>
          Current tier: <strong>{currentTier}</strong>.{" "}
          {affiliate.terminated_at &&
            `Terminated at ${affiliate.terminated_at}.`}
          {pending &&
            `Scheduled tier: ${pending.tier}, effective ${pending.effective_at}. Further tier changes are locked during notice.`}
        </p>
      )}
      <h3>Schedule a downgrade</h3>
      <p>
        The lower tier’s commission share and higher fee floors begin after
        seven days. Linked fees below the new floor increase then. Existing
        announced fee changes keep their original dates. Pricing edits lock
        during the notice period; the backend supplies the effective date.
      </p>
      <form onSubmit={schedule}>
        <fieldset disabled={!canDowngrade}>
          <label htmlFor="downgrade-tier">
            Lower tier
            <select
              id="downgrade-tier"
              value={tier}
              onChange={(event) => setTier(event.target.value as AffiliateTier)}
            >
              <option value="">Select a lower tier</option>
              {lowerTiers.map((candidate) => (
                <option key={candidate}>{candidate}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={!tier || !lowerTiers.includes(tier)}>
            Schedule affiliate downgrade
          </button>
        </fieldset>
      </form>
      <h3>Terminate affiliate benefits</h3>
      <p>
        Stops new commissions, signups and points spending immediately. Unused
        points and fee budgets freeze; earned cash remains payable after its
        holdback. Linked traders return to standard fees after seven days.
        Pending tier and fee changes are cancelled. Existing attribution and
        trading history remain recorded.
      </p>
      <button
        type="button"
        disabled={!canTerminate}
        onClick={() => {
          setReview(reviewKey);
          setConfirmation("");
        }}
      >
        Review affiliate termination
      </button>
      {reviewing && (
        <form onSubmit={terminate} aria-label="Confirm affiliate termination">
          <p>
            Terminate benefits for <strong>{address}</strong>.
          </p>
          <label htmlFor="termination-confirmation">
            Type the full affiliate address to confirm termination
            <input
              id="termination-confirmation"
              value={confirmation}
              autoComplete="off"
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={confirmation.toLowerCase() !== address.toLowerCase()}
          >
            Confirm termination
          </button>
          <button type="button" onClick={() => setReview("")}>
            Cancel termination
          </button>
        </form>
      )}
      <p>
        Only refreshed backend state confirms that an accepted change applied.
      </p>
    </section>
  );
}
